import React, { useEffect, useState } from "react";
import { Search, Tag, X, Info, Plus } from "lucide-react";

// Flat checkbox list — one row per policy-family GROUP (no nested reasons
// anymore). Group membership is decided server-side by keyword substring,
// so this stays small (a handful of groups) regardless of how many exact
// reason-string variants Walmart has ever sent for the same policy.
const UnpublishReasonPicker = ({ options, selected, setSelected }) => {
    const [open, setOpen] = useState(false);
    const popRef = React.useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    if (!options || options.length === 0) return null;

    const toggle = (key) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    });

    const clearAll = () => setSelected(new Set());

    return (
        <div className="relative z-[32]" ref={popRef}>
            <button onClick={() => setOpen((o) => !o)}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm max-w-[220px] truncate text-left focus:outline-none focus:border-teal-500 bg-white">
                {selected.size === 0 ? "Any Unpublish Reason" : `${selected.size} reason group${selected.size === 1 ? "" : "s"}`}
            </button>

            {open && (
                <div className="absolute z-30 top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg w-64 p-2">
                    {selected.size > 0 && (
                        <button onClick={clearAll} className="w-full text-left text-xs font-semibold text-red-500 hover:text-red-600 px-2 py-1.5 mb-1 border-b border-gray-50">
                            Clear all selected
                        </button>
                    )}
                    {options.map((g) => (
                        <label key={g.key} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer">
                            <input type="checkbox" checked={selected.has(g.key)} onChange={() => toggle(g.key)} className="w-3.5 h-3.5 accent-teal-600" />
                            <span className="text-xs font-semibold text-gray-700 flex-1">{g.label}</span>
                            <span className="text-[10px] text-gray-400">({g.count})</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

// Column list for the generic "+ Add Filter" picker.
const buildFilterableColumns = (costDefinitions = [], fieldDefinitions = []) => [
    { id: "title", label: "Product Title", type: "text" },
    { id: "sku", label: "SKU", type: "text" },
    { id: "upc_gtin", label: "UPC / GTIN", type: "text" },
    { id: "supplier", label: "Supplier", type: "text" },
    { id: "notes", label: "Notes", type: "text" },
    { id: "expiration_date", label: "Expiration Date", type: "date_range" },
    { id: "total_fees", label: "Total Fees", type: "number_range" },
    { id: "estimated_profit", label: "Est. Profit", type: "number_range" },
    { id: "estimated_roi", label: "Est. ROI (%)", type: "number_range" },
    ...costDefinitions.map((c) => ({ id: `cost:${c.id}`, label: c.name, type: "number_range" })),
    ...fieldDefinitions.map((f) => ({ id: `field:${f.id}`, label: f.name, type: "text" })),
];

const emptyValueForType = (type) => {
    if (type === "number_range") return { min: "", max: "" };
    if (type === "date_range") return { from: "", to: "" };
    return "";
};

// Row of active custom-filter chips + the "+ Add Filter" picker.
const CustomFilterBar = ({ customFilters, setCustomFilters, costDefinitions, fieldDefinitions }) => {
    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerRef = React.useRef(null);
    const columns = buildFilterableColumns(costDefinitions, fieldDefinitions);
    const availableColumns = columns.filter((c) => !customFilters.some((f) => f.columnId === c.id));

    useEffect(() => {
        if (!pickerOpen) return;
        const handler = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [pickerOpen]);

    const addFilter = (col) => {
        setCustomFilters((prev) => [...prev, { id: `${col.id}-${Date.now()}`, columnId: col.id, label: col.label, type: col.type, value: emptyValueForType(col.type) }]);
        setPickerOpen(false);
    };

    const removeFilter = (id) => setCustomFilters((prev) => prev.filter((f) => f.id !== id));
    const updateFilterValue = (id, value) => setCustomFilters((prev) => prev.map((f) => (f.id === id ? { ...f, value } : f)));

    return (
        <div className="ml-4 pl-6 border-l border-gray-50 flex flex-wrap items-center gap-2">
            {customFilters.map((f) => (
                <div key={f.id} className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-2.5 py-1.5">
                    <span className="text-[11px] font-bold text-gray-500">{f.label}</span>
                    {f.type === "text" && (
                        <input
                            value={f.value}
                            onChange={(e) => updateFilterValue(f.id, e.target.value)}
                            placeholder="contains..."
                            className="border border-gray-200 rounded-lg px-2 py-1 text-xs w-28 focus:outline-none focus:border-teal-500"
                        />
                    )}
                    {f.type === "number_range" && (
                        <div className="flex items-center gap-1">
                            <input type="number" value={f.value.min}
                                onChange={(e) => updateFilterValue(f.id, { ...f.value, min: e.target.value })}
                                placeholder="Min" className="border border-gray-200 rounded-lg px-2 py-1 text-xs w-16 focus:outline-none focus:border-teal-500" />
                            <span className="text-gray-300 text-xs">–</span>
                            <input type="number" value={f.value.max}
                                onChange={(e) => updateFilterValue(f.id, { ...f.value, max: e.target.value })}
                                placeholder="Max" className="border border-gray-200 rounded-lg px-2 py-1 text-xs w-16 focus:outline-none focus:border-teal-500" />
                        </div>
                    )}
                    {f.type === "date_range" && (
                        <div className="flex items-center gap-1">
                            <input type="date" value={f.value.from}
                                onChange={(e) => updateFilterValue(f.id, { ...f.value, from: e.target.value })}
                                className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-teal-500" />
                            <span className="text-gray-300 text-xs">–</span>
                            <input type="date" value={f.value.to}
                                onChange={(e) => updateFilterValue(f.id, { ...f.value, to: e.target.value })}
                                className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-teal-500" />
                        </div>
                    )}
                    <button onClick={() => removeFilter(f.id)} className="text-gray-300 hover:text-red-500"><X size={13} /></button>
                </div>
            ))}

            <div className="relative" ref={pickerRef}>
                <button onClick={() => setPickerOpen((o) => !o)}
                    className="flex items-center gap-1 text-xs font-bold text-teal-600 hover:text-teal-700 border border-dashed border-teal-300 rounded-xl px-2.5 py-1.5">
                    <Plus size={13} /> Add Filter
                </button>
                {pickerOpen && (
                    <div className="absolute z-[9999] top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg w-56 max-h-72 overflow-y-auto p-1.5">
                        {availableColumns.length === 0 ? (
                            <p className="text-xs text-gray-400 px-2 py-2">All columns already have a filter.</p>
                        ) : availableColumns.map((col) => (
                            <button key={col.id} onClick={() => addFilter(col)}
                                className="w-full text-left text-xs text-gray-700 hover:bg-teal-50 rounded-lg px-2.5 py-1.5">
                                {col.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {customFilters.length > 0 && (
                <button onClick={() => setCustomFilters([])}
                    className="flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-red-500 transition px-1">
                    <X size={13} /> Clear filters
                </button>
            )}
        </div>
    );
};

const CatalogFilters = ({
    marketplace, setMarketplace,
    search, setSearch,
    availability, setAvailability,
    publishedStatus, setPublishedStatus,
    unpublishedReasonGroupOptions, unpublishedReasonGroups, setUnpublishedReasonGroups,
    flagDefinitions, activeFlags, toggleFlag,
    brand, setBrand,
    notesFilter, setNotesFilter,
    priceMin, setPriceMin,
    priceMax, setPriceMax,
    sfStockMin, setSfStockMin,
    sfStockMax, setSfStockMax,
    wfsStockMin, setWfsStockMin,
    wfsStockMax, setWfsStockMax,
    autoScrollEnabled, setAutoScrollEnabled,
    customFilters, setCustomFilters, costDefinitions, fieldDefinitions,
}) => {
    const advancedActiveCount =
        (brand ? 1 : 0) +
        (notesFilter ? 1 : 0) +
        (priceMin ? 1 : 0) +
        (priceMax ? 1 : 0) +
        (sfStockMin ? 1 : 0) +
        (sfStockMax ? 1 : 0) +
        (wfsStockMin ? 1 : 0) +
        (wfsStockMax ? 1 : 0);

    const clearAdvanced = () => {
        setBrand("");
        setNotesFilter("");
        setPriceMin("");
        setPriceMax("");
        setSfStockMin("");
        setSfStockMax("");
        setWfsStockMin("");
        setWfsStockMax("");
    };

    return (
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-5">
            {/* Row 1 — everything: search, dropdowns, notes, price range, clear */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative w-96">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search title, SKU, UPC..."
                        className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                    />
                </div>

                <select value={marketplace} onChange={(e) => setMarketplace(e.target.value)}
                    className="border border-gray-200 rounded-xl px-2 py-2 text-sm focus:outline-none focus:border-teal-500">
                    <option value="">All Marketplaces</option>
                    <option value="walmart">Walmart</option>
                    <option value="amazon">Amazon</option>
                    <option value="ebay">eBay</option>
                </select>

                <select value={availability} onChange={(e) => setAvailability(e.target.value)}
                    className="border border-gray-200 rounded-xl px-2 py-2 text-sm focus:outline-none focus:border-teal-500">
                    <option value="">Any Availability</option>
                    <option value="In_stock">In Stock</option>
                    <option value="Out_of_stock">Out of Stock</option>
                    <option value="Preorder">Preorder</option>
                </select>

                <select value={publishedStatus} onChange={(e) => setPublishedStatus(e.target.value)}
                    className="border border-gray-200 rounded-xl px-2 py-2 text-sm focus:outline-none focus:border-teal-500">
                    <option value="">Any Publish Status</option>
                    <option value="PUBLISHED">Published</option>
                    <option value="UNPUBLISHED">Unpublished</option>
                    <option value="SYSTEM_PROBLEM">System Problem</option>
                    <option value="READY_TO_PUBLISH">Ready to Publish</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="STAGE">Stage</option>
                </select>

                <UnpublishReasonPicker options={unpublishedReasonGroupOptions} selected={unpublishedReasonGroups} setSelected={setUnpublishedReasonGroups} />

                <input
                    value={notesFilter}
                    onChange={(e) => setNotesFilter(e.target.value)}
                    placeholder="Notes contain..."
                    className="border border-gray-200 rounded-xl px-3 py-2 text-sm w-32 focus:outline-none focus:border-teal-500"
                />

                <div className="flex items-center gap-1">
                    <input
                        type="number"
                        value={priceMin}
                        onChange={(e) => setPriceMin(e.target.value)}
                        placeholder="Min $"
                        className="border border-gray-200 rounded-xl px-2 py-2 text-sm w-24 focus:outline-none focus:border-teal-500"
                    />
                    <span className="text-gray-300 text-sm">–</span>
                    <input
                        type="number"
                        value={priceMax}
                        onChange={(e) => setPriceMax(e.target.value)}
                        placeholder="Max $"
                        className="border border-gray-200 rounded-xl px-2 py-2 text-sm w-24 focus:outline-none focus:border-teal-500"
                    />
                </div>

                <div className="flex items-center gap-1">
                    <span className="text-[11px] text-gray-400">SF</span>
                    <input
                        type="number"
                        value={sfStockMin}
                        onChange={(e) => setSfStockMin(e.target.value)}
                        placeholder="Min"
                        className="border border-gray-200 rounded-xl px-2 py-2 text-sm w-14 focus:outline-none focus:border-teal-500"
                    />
                    <span className="text-gray-300 text-sm">–</span>
                    <input
                        type="number"
                        value={sfStockMax}
                        onChange={(e) => setSfStockMax(e.target.value)}
                        placeholder="Max"
                        className="border border-gray-200 rounded-xl px-2 py-2 text-sm w-14 focus:outline-none focus:border-teal-500"
                    />
                </div>

                <div className="flex items-center gap-1">
                    <span className="text-[11px] text-gray-400">WFS</span>
                    <input
                        type="number"
                        value={wfsStockMin}
                        onChange={(e) => setWfsStockMin(e.target.value)}
                        placeholder="Min"
                        className="border border-gray-200 rounded-xl px-2 py-2 text-sm w-14 focus:outline-none focus:border-teal-500"
                    />
                    <span className="text-gray-300 text-sm">–</span>
                    <input
                        type="number"
                        value={wfsStockMax}
                        onChange={(e) => setWfsStockMax(e.target.value)}
                        placeholder="Max"
                        className="border border-gray-200 rounded-xl px-2 py-2 text-sm w-14 focus:outline-none focus:border-teal-500"
                    />
                </div>

                <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none px-1 relative group">
                    <input
                        type="checkbox"
                        checked={autoScrollEnabled}
                        onChange={(e) => setAutoScrollEnabled(e.target.checked)}
                        className="w-3.5 h-3.5 accent-teal-500"
                    />
                    Auto Scroll
                    <Info size={12} className="text-gray-300" />
                    <span className="pointer-events-none absolute z-30 bottom-full left-0 mb-2 w-56 bg-gray-800 text-white text-[11px] leading-relaxed rounded-lg px-2.5 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        When enabled, hover near the left or right edge of the table for about a second to scroll it horizontally.
                        <span className="absolute top-full left-3 border-4 border-transparent border-t-gray-800" />
                    </span>
                </label>

                {advancedActiveCount > 0 && (
                    <button onClick={clearAdvanced}
                        className="flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-red-500 transition px-1">
                        <X size={13} /> Clear
                    </button>
                )}
            </div>

            {/* Row 2 — tags, always visible */}
            <div className="mt-3 pt-3 border-t border-gray-50 flex flex-wrap items-center gap-2">
                {flagDefinitions.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                        <Tag size={14} className="text-gray-400 flex-shrink-0" />
                        {flagDefinitions.map((f) => {
                            const active = activeFlags.includes(f.name);
                            return (
                                <button key={f.id} onClick={() => toggleFlag(f.name)}
                                    style={active ? { backgroundColor: f.color, borderColor: f.color } : { borderColor: f.color, color: f.color }}
                                    className={`px-3 py-1 rounded-full text-xs font-bold border transition ${active ? "text-white" : "bg-white"}`}>
                                    {f.name}
                                </button>
                            );
                        })}
                        {activeFlags.length > 0 && (
                            <button onClick={() => activeFlags.forEach((name) => toggleFlag(name))}
                                className="flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-red-500 transition ml-1">
                                <X size={13} /> Clear tags
                            </button>
                        )}
                    </div>
                )}

                <CustomFilterBar
                    customFilters={customFilters}
                    setCustomFilters={setCustomFilters}
                    costDefinitions={costDefinitions}
                    fieldDefinitions={fieldDefinitions}
                />
            </div>
        </div>
    );
};

export default CatalogFilters;